import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    CardContent,
    Divider,
    Typography,
    Snackbar,
    Alert,
    Skeleton,
    Box,
    Grid,
    TextField,
    FormControlLabel,
    Checkbox,
    MenuItem,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { H4 } from "app/components/Typography";
import { LoadingButton } from "@mui/lab";
import { Save, PhotoCamera, Delete } from "@mui/icons-material";
import { API_URI } from "config";
import Axios from "axios";
import useAuth from "app/hooks/useAuth";
import AccessRestricted from "app/components/AccessRestricted";
import { STATES, countries } from "app/utils/constant";

const HiddenInput = styled("input")({ display: "none" });
const ImageContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    display: "inline-block",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    boxShadow: theme.shadows[2],
}));
const UploadArea = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
}));

const StyledCard = styled(Card)(({ theme }) => ({
    maxWidth: "700px",
    margin: "0 auto",
    marginTop: theme.spacing(2),
    borderRadius: 16,
    overflow: "hidden",
}));

const ORGANIZATION_TYPES = [
    "Aged Care",
    "Business & Professional",
    "Cultural & Arts",
    "Educational & Learning",
    "Health & Wellbeing",
    "Other",
    "Religious & Spiritual",
    "Social & Community",
    "Sports",
    "Youth & Community",
];

const emptyAddress = () => ({
    address_line_2: "",
    streetAddress: "",
    city: "",
    state: "",
    country: "",
    postCode: "",
});

const emptyPostal = () => ({
    is_same_as_business: true,
    address_line_2: "",
    street: "",
    city: "",
    state: "",
    country: "",
    postCode: "",
});

const emptyContact = () => ({
    phone: "",
    email: "",
    title: "",
    firstName: "",
    lastName: "",
    position: "",
});

const ClubDetailsForm = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [form, setForm] = useState({
        name: "",
        knownAs: "",
        organization_type: "Other",
        organization_industry: "",
        businessInfo: {
            address: emptyAddress(),
            postalAddress: emptyPostal(),
            legalName: "",
            abn_acn: "",
        },
        contact: emptyContact(),
    });
    const [logoCloudData, setLogoCloudData] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);

    const { user } = useAuth();

    const canAccess =
        user?.role === "ADMIN" || user?.permission?.includes("club_owner");
    const clubId = user?.clubId?._id || user?.clubId;

    const fetchDetails = useCallback(async () => {
        if (!clubId) return;
        setLoading(true);
        try {
            const { data } = await Axios.get(
                `${API_URI}/api/club/${clubId}/details`,
                {}
            );
            if (data?.success && data?.club) {
                const c = data.club;
                setForm({
                    name: c.name ?? "",
                    knownAs: c.knownAs ?? "",
                    organization_type: c.organization_type ?? "Other",
                    organization_industry: c.organization_industry ?? "",
                    businessInfo: {
                        address: {
                            ...emptyAddress(),
                            ...(c.businessInfo?.address || {}),
                        },
                        postalAddress: {
                            ...emptyPostal(),
                            ...(c.businessInfo?.postalAddress || {}),
                        },
                        legalName: c.businessInfo?.legalName ?? "",
                        abn_acn: c.businessInfo?.abn_acn ?? "",
                    },
                    contact: {
                        ...emptyContact(),
                        ...(c.contact || {}),
                    },
                });
                const logo = c.settings?.theme?.logo;
                if (logo && typeof logo === "object" && logo.Location) {
                    setLogoCloudData(logo);
                } else {
                    setLogoCloudData(null);
                }
            }
        } catch (error) {
            //console.error("Error fetching client details:", error);
            setSnackbar({
                open: true,
                message:
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to load client details",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        if (canAccess && clubId) fetchDetails();
    }, [canAccess, clubId, fetchDetails]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleChange = (path, value) => {
        setForm((prev) => {
            const next = { ...prev };
            if (path === "name") next.name = value;
            else if (path === "knownAs") next.knownAs = value;
            else if (path === "organization_type")
                next.organization_type = value;
            else if (path === "organization_industry")
                next.organization_industry = value;
            else if (path.startsWith("address.")) {
                const key = path.replace("address.", "");
                next.businessInfo = { ...next.businessInfo };
                next.businessInfo.address = {
                    ...next.businessInfo.address,
                    [key]: value,
                };
            } else if (path.startsWith("postal.")) {
                const key = path.replace("postal.", "");
                next.businessInfo = { ...next.businessInfo };
                next.businessInfo.postalAddress = {
                    ...next.businessInfo.postalAddress,
                    [key]: value,
                };
            } else if (path === "postal.is_same_as_business") {
                next.businessInfo = { ...next.businessInfo };
                next.businessInfo.postalAddress = {
                    ...next.businessInfo.postalAddress,
                    is_same_as_business: value,
                };
            } else if (path === "legalName") {
                next.businessInfo = { ...next.businessInfo, legalName: value };
            } else if (path === "abn_acn") {
                next.businessInfo = { ...next.businessInfo, abn_acn: value };
            } else if (path.startsWith("contact.")) {
                const key = path.replace("contact.", "");
                next.contact = { ...next.contact, [key]: value };
            }
            return next;
        });
    };

    const validateLogoFile = (file) => {
        const MAX = 5 * 1024 * 1024;
        const types = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
        if (!file) return "No file selected";
        if (file.size > MAX) return "File must be less than 5MB";
        if (!types.includes(file.type)) return "Only JPEG, PNG, and GIF allowed";
        return null;
    };

    const handleLogoUpload = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;
        const err = validateLogoFile(file);
        if (err) {
            setSnackbar({ open: true, message: err, severity: "error" });
            return;
        }
        const fd = new FormData();
        fd.append("logo", file);
        setLogoUploading(true);
        try {
            const { data } = await Axios.post(
                `${API_URI}/api/admin/upload-logo/sup`,
                fd,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setLogoCloudData(data);
            setSnackbar({
                open: true,
                message: "Logo uploaded. Save client details to apply.",
                severity: "success",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || "Logo upload failed",
                severity: "error",
            });
        } finally {
            setLogoUploading(false);
        }
        e.target.value = "";
    };

    const handleLogoRemove = () => {
        setLogoCloudData(null);
        setSnackbar({
            open: true,
            message: "Logo removed. Save client details to apply.",
            severity: "info",
        });
    };

    const handleSave = async () => {
        if (!clubId) {
            setSnackbar({
                open: true,
                message: "Client information not available",
                severity: "error",
            });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                settings: {
                    theme: {
                        logo:
                            logoCloudData && Object.keys(logoCloudData).length > 0
                                ? logoCloudData
                                : {},
                    },
                },
            };
            await Axios.put(
                `${API_URI}/api/club/${clubId}/details`,
                payload,
                {}
            );
            setSnackbar({
                open: true,
                message: "Organisation details saved successfully",
                severity: "success",
            });
        } catch (error) {
            //console.error("Error saving client details:", error);
            setSnackbar({
                open: true,
                message:
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    "Failed to save client details",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    if (!canAccess) return <AccessRestricted />;

    if (loading) {
        return (
            <StyledCard elevation={3}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <Skeleton variant="circular" width={48} height={48} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="60%" height={32} />
                            <Skeleton variant="text" width="40%" height={24} />
                        </Box>
                    </Box>
                    <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
                </CardContent>
            </StyledCard>
        );
    }

    const addr = form.businessInfo?.address || emptyAddress();
    const postal = form.businessInfo?.postalAddress || emptyPostal();
    const contact = form.contact || emptyContact();
    const sameAsBusiness = !!postal.is_same_as_business;

    return (
        <>
            <StyledCard elevation={3}>
                <Box p={3}>
                    <H4>Client Details</H4>
                </Box>
                <Divider />
                <CardContent sx={{ p: 3 }}>
                    <H4 sx={{ mb: 2 }}>Client logo</H4>
                    <Divider sx={{ mb: 2 }} />
                    {logoCloudData?.Location ? (
                        <ImageContainer>
                            <img
                                src={logoCloudData.Location}
                                alt="Client logo"
                                style={{
                                    width: "200px",
                                    height: "auto",
                                    maxHeight: "150px",
                                    objectFit: "contain",
                                }}
                            />
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    backgroundColor: "rgba(0,0,0,0.7)",
                                    borderRadius: "50%",
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleLogoRemove}
                                    sx={{ color: "white" }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        </ImageContainer>
                    ) : (
                        <label htmlFor="client-logo-upload">
                            <HiddenInput
                                id="client-logo-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                disabled={logoUploading}
                            />
                            <UploadArea component="div">
                                {logoUploading ? (
                                    <Box>
                                        <CircularProgress size={24} sx={{ mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Uploading...
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <PhotoCamera
                                            sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Click to upload client logo
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            Max 5MB • JPEG, PNG, GIF
                                        </Typography>
                                    </Box>
                                )}
                            </UploadArea>
                        </label>
                    )}

                    <H4 sx={{ mt: 4, mb: 2 }}>Organisation</H4>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Organisation name"
                                value={form.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Known as"
                                value={form.knownAs}
                                onChange={(e) => handleChange("knownAs", e.target.value)}
                                helperText="Short name (e.g. ACME for Acme Corporation)"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Organisation type"
                                value={form.organization_type}
                                onChange={(e) =>
                                    handleChange("organization_type", e.target.value)
                                }
                            >
                                {ORGANIZATION_TYPES.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Industry"
                                value={form.organization_industry}
                                onChange={(e) =>
                                    handleChange("organization_industry", e.target.value)
                                }
                            />
                        </Grid>
                    </Grid>

                    <H4 sx={{ mt: 4, mb: 2 }}>Contact information</H4>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Title"
                                value={contact.title || ""}
                                onChange={(e) =>
                                    handleChange("contact.title", e.target.value)
                                }
                                placeholder="e.g. Mr, Ms, Dr"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Position"
                                value={contact.position || ""}
                                onChange={(e) =>
                                    handleChange("contact.position", e.target.value)
                                }
                                placeholder="e.g. Account Manager"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="First name"
                                value={contact.firstName || ""}
                                onChange={(e) =>
                                    handleChange("contact.firstName", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Last name"
                                value={contact.lastName || ""}
                                onChange={(e) =>
                                    handleChange("contact.lastName", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Email"
                                type="email"
                                value={contact.email || ""}
                                onChange={(e) =>
                                    handleChange("contact.email", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Phone"
                                value={contact.phone || ""}
                                onChange={(e) =>
                                    handleChange("contact.phone", e.target.value)
                                }
                            />
                        </Grid>
                    </Grid>

                    <H4 sx={{ mt: 4, mb: 2 }}>Business address</H4>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Address line 1"
                                value={addr.streetAddress || ""}
                                onChange={(e) =>
                                    handleChange("address.streetAddress", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Address line 2"
                                value={addr.address_line_2 || ""}
                                onChange={(e) =>
                                    handleChange("address.address_line_2", e.target.value)
                                }
                                placeholder="Suite, unit, building, floor, etc."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="City"
                                value={addr.city || ""}
                                onChange={(e) =>
                                    handleChange("address.city", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Country"
                                value={addr.country || ""}
                                onChange={(e) =>
                                    handleChange("address.country", e.target.value)
                                }
                            >
                                <MenuItem value="">
                                    <em>Select country</em>
                                </MenuItem>
                                {countries.map((item) => (
                                    <MenuItem key={item.code} value={item.name}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="State"
                                value={addr.state || ""}
                                onChange={(e) =>
                                    handleChange("address.state", e.target.value)
                                }
                            >
                                <MenuItem value="">
                                    <em>Select state</em>
                                </MenuItem>
                                {STATES.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Post code"
                                value={addr.postCode || ""}
                                onChange={(e) =>
                                    handleChange("address.postCode", e.target.value)
                                }
                            />
                        </Grid>
                    </Grid>

                    <H4 sx={{ mt: 4, mb: 2 }}>Postal address</H4>
                    <Divider sx={{ mb: 2 }} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sameAsBusiness}
                                onChange={(e) =>
                                    handleChange(
                                        "postal.is_same_as_business",
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label="Same as business address"
                    />
                    {!sameAsBusiness && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Address line 1"
                                    value={postal.street || ""}
                                    onChange={(e) =>
                                        handleChange("postal.street", e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Address line 2"
                                    value={postal.address_line_2 || ""}
                                    onChange={(e) =>
                                        handleChange(
                                            "postal.address_line_2",
                                            e.target.value
                                        )
                                    }
                                    placeholder="Suite, unit, building, floor, etc."
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="City"
                                    value={postal.city || ""}
                                    onChange={(e) =>
                                        handleChange("postal.city", e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Country"
                                    value={postal.country || ""}
                                    onChange={(e) =>
                                        handleChange("postal.country", e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        <em>Select country</em>
                                    </MenuItem>
                                    {countries.map((item) => (
                                        <MenuItem key={item.code} value={item.name}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="State"
                                    value={postal.state || ""}
                                    onChange={(e) =>
                                        handleChange("postal.state", e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        <em>Select state</em>
                                    </MenuItem>
                                    {STATES.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Post code"
                                    value={postal.postCode || ""}
                                    onChange={(e) =>
                                        handleChange("postal.postCode", e.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    )}

                    <H4 sx={{ mt: 4, mb: 2 }}>Legal & tax</H4>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Legal name"
                                value={form.businessInfo?.legalName || ""}
                                onChange={(e) =>
                                    handleChange("legalName", e.target.value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="ABN / ACN"
                                value={form.businessInfo?.abn_acn || ""}
                                onChange={(e) =>
                                    handleChange("abn_acn", e.target.value)
                                }
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 4 }}>
                        <LoadingButton
                            variant="contained"
                            loading={saving}
                            onClick={handleSave}
                            startIcon={<Save />}
                            sx={{ borderRadius: 2 }}
                        >
                            Save client details
                        </LoadingButton>
                    </Box>
                </CardContent>
            </StyledCard>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ClubDetailsForm;
